import { useEffect, useMemo, useState } from 'react';

import { useProductDependencies } from '@atlassian/company-hub-common';

import type { Card, CardsExtensionParameters } from '../../types';
import { useSearchPageResults } from '../use-search-page-results';

export const useCards = (
	parameters: CardsExtensionParameters,
): {
	cards: Card[];
	error: Error | null;
	loading: boolean;
} => {
	const { cardCount, filters } = parameters;
	const [cards, setCards] = useState<Card[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);

	const { submitError } = useProductDependencies();
	const { getDynamicCards } = useSearchPageResults();

	useEffect(() => {
		if (!!cardCount && !!filters) {
			setLoading(true);
			setError(null);

			let isActive = true;

			void getDynamicCards(cardCount, filters)
				.then(({ cards: dynamicCards, errors: aggErrors }) => {
					if (!isActive) {
						return;
					}

					if (aggErrors) {
						const error = new Error(JSON.stringify(aggErrors));

						if (dynamicCards.length === 0) {
							setError(error);
						} else {
							submitError?.(error);
						}
					}
					setCards(dynamicCards);
				})
				.catch((err) => {
					if (!isActive) {
						return;
					}

					setError(err);
				})
				.finally(() => {
					if (!isActive) {
						return;
					}

					setLoading(false);
				});

			return () => {
				isActive = false;
			};
		}
	}, [getDynamicCards, cardCount, filters, submitError]);

	return useMemo(() => ({ cards, loading, error }), [cards, loading, error]);
};
